import { AxiosError, AxiosResponse } from 'axios';
import instance, { ApiError } from '../../api/instance';
import { Collection, CollectionLocale } from './collections.types';
import { ContentCard } from 'src/features/contents/contents.types';
import { UploadFileToBucket } from 'src/api/utils/uploadFileToBucket';

const collectionsApiPath = '/content/collections';

export type ContentQueryParams = {
  name?: string;
};

export const getCollections = async ({
  params,
}: {
  params?: ContentQueryParams;
}): Promise<AxiosResponse<Collection[] & ApiError>> => {
  return instance
    .get<Collection[]>(collectionsApiPath, { params })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const createCollection = async ({ name }: { name: string }): Promise<AxiosResponse<Collection & ApiError>> => {
  return instance
    .post<Collection>(collectionsApiPath, { name: name })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const collectionApiPath = (id: string) => `/content/collections/${id}`;

export const getCollection = async ({
  collectionId,
}: {
  collectionId: string;
}): Promise<AxiosResponse<Collection & ApiError>> => {
  return instance
    .get<Collection>(collectionApiPath(collectionId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const updateCollection = async ({
  collectionId,
  data,
}: {
  collectionId: string;
  data: Partial<Collection>;
}): Promise<AxiosResponse<Collection & ApiError>> => {
  return instance
    .post<Collection>(collectionApiPath(collectionId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const deleteCollection = async ({
  collectionId,
}: {
  collectionId: string;
}): Promise<AxiosResponse<Collection & ApiError>> => {
  return instance
    .delete<Collection>(collectionApiPath(collectionId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

const collectionCardsApiPath = (id: string) => `/content/collections/${id}/cards`;

export const getCollectionCards = async ({
  collectionId,
}: {
  collectionId: string;
}): Promise<AxiosResponse<ContentCard[] & ApiError>> => {
  return instance
    .get<ContentCard[]>(collectionCardsApiPath(collectionId))
    .catch(e => e.response as AxiosError<ApiError> as never);
};

type CardIds = {
  ids: string[];
};
export const addCardsToCollection = async ({
  collectionId,
  data,
}: {
  collectionId: string;
  data: CardIds;
}): Promise<AxiosResponse<ContentCard[] & ApiError>> => {
  return instance
    .post<ContentCard[]>(collectionCardsApiPath(collectionId), data)
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export const removeCardsFromCollection = async ({
  collectionId,
  data,
}: {
  collectionId: string;
  data: CardIds;
}): Promise<AxiosResponse<void & ApiError>> => {
  return instance
    .delete(collectionCardsApiPath(collectionId), { data })
    .catch(e => e.response as AxiosError<ApiError> as never);
};

export type CollectionCardImage = (params: {
  collectionId: string;
  data: { filename: string };
}) => Promise<AxiosResponse<Parameters<UploadFileToBucket>[0]>>;

const collectionImageApiPath = (contentId: string) => `/content/collections/${contentId}/images`;

export const uploadCollectionImage: CollectionCardImage = async ({ collectionId, data }) => {
  return instance.post(collectionImageApiPath(collectionId), data).catch(e => e.response as never);
};

const collectionLocalesApiPath = (collectionId: string) => `/content/collections/${collectionId}/translations`;

export const getCollectionLocalesAPI = async ({
  collectionId,
}: {
  collectionId: string;
}): Promise<AxiosResponse<CollectionLocale[] & ApiError>> => {
  return instance.get(collectionLocalesApiPath(collectionId)).catch(e => e.response as never);
};

export const createCollectionLocaleAPI = async ({
  collectionId,
  data,
}: {
  collectionId: string;
  data: Omit<CollectionLocale, 'id'>;
}): Promise<AxiosResponse<CollectionLocale & ApiError>> => {
  return instance.post<CollectionLocale>(collectionLocalesApiPath(collectionId), data).catch(e => e.response as never);
};

const collectionLocaleApiPath = (collectionId: string, localeId: string) =>
  `/content/collections/${collectionId}/translations/${localeId}`;

export const updateCollectionLocaleAPI = async ({
  collectionId,
  localeId,
  data,
}: {
  collectionId: string;
  localeId: string;
  data: Omit<CollectionLocale, 'id'>;
}): Promise<AxiosResponse<CollectionLocale & ApiError>> => {
  return instance
    .post<CollectionLocale>(collectionLocaleApiPath(collectionId, localeId), data)
    .catch(e => e.response as never);
};

export const deleteCollectionLocaleAPI = async ({
  collectionId,
  localeId,
}: {
  collectionId: string;
  localeId: string;
}): Promise<AxiosResponse<void & ApiError>> => {
  return instance.delete(collectionLocaleApiPath(collectionId, localeId)).catch(e => e.response as never);
};
